import { useCallback, useState } from 'react';

import Workspace from 'src/entities/workspace';
import { getFileName } from 'src/lib/fileUtils';
import useSnackbarSystem from '@ui/systems/useSnackbarSystem';
import useUpdateWorkspaceMutation from 'src/new_arch/modules/workspaces/queries/useUpdateWorkspaceMutation';
import useDeleteWorkspaceLogoMutation from 'src/new_arch/modules/workspaces/queries/useDeleteWorkspaceLogoMutation';
import useReduxPopupSystem from 'src/new_arch/hooks/useReduxPopupSystem';
import PricingPlanFeature from 'src/entities/pricing/pricingPlanFeature';
import { trackWorkspaceLogoRestored, trackWorkspaceLogoUploaded } from 'src/services/tracker/trackers/workspaces';
import { convertSVGtoPNG, isSVGFile } from 'src/new_arch/lib/fileUtils';

const SUPPORTED_IMAGE_TYPES = [ 'image/png', 'image/svg+xml' ];
const PLACEHOLDER_TEXT = 'Click to upload (PNG, SVG - Max 500KB). Recommended size: 400x100px (rectangular, 4:1 aspect ratio).';

const LOGO_MAX_SIZE = 500 * 1024; // 500KB

const validateFileType = ( file: File ) => SUPPORTED_IMAGE_TYPES.includes( file.type );
const validateFileSize = ( file: File ) => file.size <= LOGO_MAX_SIZE;

const useEditWorkspaceLogo = ( { workspace, readOnly }: { workspace: Workspace, readOnly: boolean } ) => {
	const [ logo, setLogo ] = useState<File | null>( null );
	const snackbarSystem = useSnackbarSystem();
	const popupSystem = useReduxPopupSystem();

	const { mutate: updateWorkspace, isLoading: isSaving } = useUpdateWorkspaceMutation( {
		workspaceID: workspace.id,
		onSuccess: () => {
			trackWorkspaceLogoUploaded( { workspaceID: workspace.id } );
			snackbarSystem.showSuccessMessage( { title: 'Success! Your changes have been saved!' } );
			setLogo( null );
		},
		onError: () => {
			snackbarSystem.showErrorMessage( { title: 'Oops, something went wrong! Try again.' } );
		}
	} );

	const { mutate: deleteWorkspaceLogo, isLoading: isDeletingLogo } = useDeleteWorkspaceLogoMutation( {
		workspaceID: workspace.id,
		onSuccess: () => {
			trackWorkspaceLogoRestored( { workspaceID: workspace.id } );
			snackbarSystem.showSuccessMessage( { title: 'Settings have been restored to default.' } );
			setLogo( null );
		},
		onError: () => {
			snackbarSystem.showErrorMessage( { title: 'Oops, something went wrong! Try again.' } );
		}
	} );

	const placeholder = workspace.logo
		? `${getFileName( workspace.logo )}`
		: PLACEHOLDER_TEXT;

	const onFileChange = useCallback( async ( file: File | null ) => {
		if ( readOnly ) return;
		if ( !file ) return;

		if ( !validateFileType( file ) ) {
			snackbarSystem.showErrorMessage( { title: 'Only PNG and SVG images are supported.' } );
			return;
		}

		if ( !validateFileSize( file ) ) {
			snackbarSystem.showErrorMessage( { title: 'File size must be less than 500KB.' } );
			return;
		}

		if ( isSVGFile( file ) ) {
			try {
				const pngFile = await convertSVGtoPNG( file );

				if ( !validateFileSize( pngFile ) ) {
					snackbarSystem.showErrorMessage( { title: 'Converted PNG file size must be less than 500KB.' } );
					return;
				}

				setLogo( pngFile );
			} catch ( error ) {
				snackbarSystem.showErrorMessage( { title: 'Failed to convert SVG to PNG. Please try a different file.' } );
			}
		} else {
			setLogo( file );
		}
	}, [ setLogo, readOnly, snackbarSystem ] );

	const preview = logo ? URL.createObjectURL( logo ) : workspace.logo;

	const isFeatureAvailable = workspace.supportsFeature( PricingPlanFeature.WORKSPACE_CUSTOM_BRANDING );

	const saveButtonDisabled = readOnly || !logo || !isFeatureAvailable;
	const fileInputDisabled = readOnly || !isFeatureAvailable;
	const restoreDefaultButtonDisabled = readOnly || !workspace.logo || !isFeatureAvailable;

	const onSaveClick = useCallback( () => {
		if ( saveButtonDisabled ) return;
		updateWorkspace( { logo } );
	}, [ updateWorkspace, logo, saveButtonDisabled ] );

	const onCancelClick = useCallback( () => {
		setLogo( null );
	}, [] );

	const onRestoreDefaultClick = useCallback( () => {
		popupSystem.showDefaultConfirmationDialog( {
			title: 'Are you sure you want to restore the default logo?',
			description: 'By restoring the default logo custom branding for your workspace will be disabled.',
			acceptText: 'Restore Default Logo',
			cancelText: 'Cancel',
			descriptionAlignment: 'left',
			acceptButtonColor: 'blue'
		} ).then( ( confirm: boolean ) => {
			if ( confirm ) { deleteWorkspaceLogo(); }
			popupSystem.hideConfirmationDialog();
		} );
	}, [ deleteWorkspaceLogo, popupSystem ] );

	const showCancelButton = !!logo;

	return {
		restoreDefaultButtonDisabled,
		logo,
		placeholder,
		onFileChange,
		preview,
		saveButtonDisabled,
		fileInputDisabled,
		onSaveClick,
		isSaving,
		onRestoreDefaultClick,
		isDeletingLogo,
		onCancelClick,
		showCancelButton,
		isFeatureAvailable
	};
};

export default useEditWorkspaceLogo;
